/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

export default function ContentDisclaimerText({ showTitle = true }: { showTitle?: boolean }) {
  return (
    <div data-testid="legal-disclaimer-text">
      {showTitle && (
        <p style={{ textAlign: 'center', fontSize: 22 }}>
          <b>MedPearl Legal Disclaimer and Notice </b>
        </p>
      )}
      <p>
        MedPearl and its proprietary programs (e.g. Clinical Knowledge Hub, Pearl, Actionable Intelligence, MedPearl
        Patient) is designed to consolidate clinical content and information into consumable quantities. The materials
        contained herein are for informational purposes only and are not meant as a substitute for a provider’s clinical
        judgement. Further, unless otherwise specified, all content that is provided, upon submission, becomes property
        of Health Endeavors, LLC.
      </p>
      <p>
        <b>Patient Information Display Limitations and Representations.</b> Where MedPearl presents certain medical
        information attributable to a particular patient, such information is limited to that which is available within
        the Customer’s electronic health record and is not guaranteed to be complete, contemporaneous, or correct. Any
        information displayed is not stored in nor maintained in the MedPearl platform nor does it constitute a medical
        record for retention purposes.
      </p>
      <p>
        <b>Independent Medical Practice Expectations.</b> MedPearl is not a substitute for medical examination and
        should only be used in conjunction with a full review of the patient’s condition and medical history/record by a
        medical professional. MedPearl does not provide medical advice and should not be used as a sole source for
        diagnosis or treatment of any condition. These topics should not be construed as dictating an exclusive course
        of treatment. Variations in practice may be warranted based on the needs of the individual patient, resources,
        and limitations unique to the institutional type of practice including the source author’s variation. Use of
        MedPearl shall neither create a physician-patient relationship nor constitute the practice of medicine. Health
        Endeavors, LLC is neither responsible for any omissions, defects or failures associated with MedPearl content
        nor any damages that may result from any such defects or failures. Neither MedPearl nor Health Endeavors, LLC
        recommends or endorses any specific tests, physicians, products, procedures, opinions, or other information that
        may be mentioned on MedPearl or in a link on MedPearl.
      </p>
      <p>
        <b>No medical advice.</b> MedPearl is not a health care provider and MedPearl does not operate as Software as a
        Medical Device. The content is not intended to provide medical advice, diagnosis, or treatment or substitute for
        an individual patient assessment. The content is for general medical education only.
      </p>
      <p>
        <b>Liability Limitations for Content Authorship.</b> Every effort is made by contributing authors and editors to
        ensure that information in these topics is accurate and up to date. Neither Health Endeavors, LLC nor any
        contributing authors or editors, accepts liability for any damages occurring from any errors or omissions in any
        information found in this platform. The final responsibility for a patient’s care lies with the Customer and its
        medical provider.
      </p>
      <p>
        <b>Limitations on Brand Inclusion Representations.</b> The inclusion of brand names of certain prescriptions or
        over the counter medications as examples in some MedPearl content does not constitute endorsement by Health
        Endeavors, LLC. MedPearl content may provide links to other sites on the Internet for your convenience. These
        other sites are maintained by third parties over which Health Endeavors, LLC exercises no control. The
        appearance of any such third-party links is not intended to endorse any particular company or product. If you
        decide to access any of the third-party sites linked to from the MedPearl platform, you do so entirely at your
        own risk. Any inclusion of medications, dosing, routing, or other specifications should be verified by the
        provider prior to prescription or use. Some topics contain discussions of ‘off-label’ drug use that is not
        approved by the Federal Drug Administration and should be considered independently by the treating provider.
      </p>
      <p>
        <b>Content Disclaimer.</b> MedPearl provides the licensed content to you on an “as-is” basis. MedPearl does not
        make any warrant of any kind, express or implied. MedPearl specifically disclaims any implied warrants of
        fitness for a particular purpose, merchantability, particular purpose, non-infringement, accuracy, quality
        correctness, completeness, comprehensiveness, suitability, system availability, and compatibility. This
        disclaimer is subject to the laws of any state that do not allow disclaimers of implied warranties. You
        acknowledge that no representations have been made respecting the licensed MedPearl content.
      </p>
      <p>
        <b>Technical Disclaimer.</b> You acknowledge that the licensed content and any information contained in the
        licensed content or any communication provided to MedPearl during use of the licensed content will be
        transmitted over a medium that is beyond the control of MedPearl and may be subject to limitations, delays,
        latency issues, and other problems inherent in the use of the internet and electronic communications, and
        MedPearl is not responsible for and assumes no liability for any delays, delivery failures, corruption of any
        data or other damage resulting from such problems. MedPearl is not responsible for and assumes no liability for
        any technical malfunction, inability to access the licensed content or other problems related to or arising from
        any third party hardware or software, including any third party website, application, or other platform.
      </p>
      <p>
        <b>Consumer Information Collection and Opt Out Policy.</b> The MedPearl platform neither collects patient
        information nor stores it. The platform may track users of the content to evaluate program efficacy, adjust
        clinical topics, and enhance performance. There is no intent to sell, advertise, or otherwise use the
        information collected, collectively referred to as consumer personal information, at this time. An individual
        who wishes to opt-out of their data collection may request to do so by emailing
        medpearlsupport@healthendeavors.com and providing written notification of their desire to not have any personal
        information collected about them. A copy of their current data profile may be obtained on request at the above
        email address. The data may still be collected and anonymized for program improvement.
      </p>
      <p>
        <b>Feedback Ownership.</b> You agree and acknowledge that any feedback provided on a clinical topic or in
        general may be used by MedPearl without restriction and for any purpose, including, but not limited to creating
        derivative works based upon that information. Feedback means your suggestions, comments, corrections,
        discoveries, ideas, improvements, insights, opinions, preferences, suggestions, or any other feedback provided
        by you to MedPearl in connection with your use.
      </p>
      <p>
        <b>Copyright and Permission to reproduce material.</b> All content in MedPearl is created by humans and subject
        to the copyright and protections associated with that work. Permission to reproduce material from MedPearl in
        another publication must be secured in writing with the express written consent of the company for external use.
      </p>
    </div>
  );
}
